export interface VideoSegment {
  index: number;
  frames: string[];
}

export const extractVideoSegments = async (
  file: File,
  fps: number,
  framesPerSegment: number,
  scale: number,
  onProgress?: (progress: number, currentFrameBase64?: string) => void
): Promise<VideoSegment[]> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    video.onloadeddata = async () => {
      try {
        const duration = video.duration;
        
        // Pomiń pierwszą i ostatnią sekundę jeśli wideo jest dłuższe (często to czarne klatki)
        const safeDuration = duration > 2 ? duration - 2 : duration;
        const safeStart = duration > 2 ? 1 : 0;
        
        // Oblicz całkowitą liczbę klatek i interwał
        const interval = 1 / fps;
        const totalFrames = Math.max(1, Math.floor(safeDuration / interval));
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error("Canvas 2D ctx error");

        // Oblicz docelowe wymiary z uwzględnieniem skali
        const targetWidth = Math.floor(video.videoWidth * scale);
        const targetHeight = Math.floor(video.videoHeight * scale);
        
        // Max limit dla modeli VLM np. 1024px
        const maxDim = 1024;
        let finalW = targetWidth;
        let finalH = targetHeight;
        
        if (finalW > maxDim || finalH > maxDim) {
          const ratio = Math.min(maxDim / finalW, maxDim / finalH);
          finalW = Math.floor(finalW * ratio);
          finalH = Math.floor(finalH * ratio);
        }

        canvas.width = finalW;
        canvas.height = finalH;

        const segments: VideoSegment[] = [];
        let currentSegmentFrames: string[] = [];
        let segmentIndex = 1;

        for (let i = 0; i < totalFrames; i++) {
          const time = safeStart + (i * interval);
          video.currentTime = Math.min(time, duration);
          
          await new Promise((res) => {
            const onSeeked = () => {
              video.removeEventListener('seeked', onSeeked);
              res(null);
            };
            video.addEventListener('seeked', onSeeked);
            if (video.seeking) {
               // still seeking
            } else {
               res(null);
            }
          });

          ctx.drawImage(video, 0, 0, finalW, finalH);
          const base64 = canvas.toDataURL('image/jpeg', 0.85); // kompresja 85% oszczędza VRAM
          
          currentSegmentFrames.push(base64);
          if (currentSegmentFrames.length >= framesPerSegment) {
            segments.push({ index: segmentIndex++, frames: [...currentSegmentFrames] });
            currentSegmentFrames = [];
          }

          if (onProgress) onProgress(Math.round(((i + 1) / totalFrames) * 100), base64);
        }

        // Dodaj pozostałe klatki jako ostatni segment
        if (currentSegmentFrames.length > 0) {
          segments.push({ index: segmentIndex, frames: currentSegmentFrames });
        }

        URL.revokeObjectURL(url);
        resolve(segments);
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };

    video.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(new Error("Video load failed"));
    };
  });
};
